import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Calendar, Clock, Trophy, DollarSign, TrendingUp, BarChart3, Activity } from 'lucide-react';
import EVCalculator from './EVCalculator';
import { LoadingSpinner } from "../../../../components/shared/loading";
import OddsMovementTab from './OddsMovementTab';
import { isMajorLeague } from '../../../../components/dashboards/utils/majorLeagues';

const FixtureOddsModal = ({ fixture, odds, onClose, loading }) => {
  const [activeTab, setActiveTab] = useState('prematch');

  // Determine which tabs to show based on fixture status
  const isLive = fixture ? ['1H', 'HT', '2H', 'ET', 'BT', 'P', 'INT', 'LIVE'].includes(fixture.fixture?.status?.short) : false;
  const isFinished = fixture ? ['FT', 'AET', 'PEN'].includes(fixture.fixture?.status?.short) : false;
  const isUpcoming = fixture ? ['NS', 'TBD'].includes(fixture.fixture?.status?.short) : false;
  
  // Set default tab based on game status - must be before any returns
  React.useEffect(() => {
    if (isLive && activeTab === 'prematch') {
      setActiveTab('live');
    } else if (!isLive && activeTab === 'live') {
      setActiveTab('prematch');
    }
  }, [isLive, activeTab]);

  if (!fixture) return null;
  
  const tabs = [
    ...(isUpcoming || !isLive ? [{ id: 'prematch', label: 'Pre-Match Odds', icon: DollarSign }] : []),
    ...(isLive ? [{ id: 'live', label: 'Live Odds', icon: TrendingUp }] : []),
    { id: 'calculator', label: 'EV Calculator', icon: BarChart3 },
    ...(isMajorLeague(fixture?.league?.id) ? [{ id: 'movement', label: 'Line Movement', icon: Activity }] : []),
  ];

  const homeTeam = fixture.teams?.home;
  const awayTeam = fixture.teams?.away;

  // Render live odds which have a different structure (no bookmakers, just bet types)
  const renderLiveOddsSection = (oddsData, title) => {
    if (!oddsData || oddsData.length === 0) {
      // More informative messages based on context
      let message = 'Live odds not available';
      let subMessage = null;
      
      if (!isLive) {
        subMessage = 'Match is not currently live';
      } else {
        subMessage = 'Live betting may not be offered for this match or league';
      }
      
      return (
        <div className="text-center py-8">
          <p className="text-white/50">{message}</p>
          {subMessage && (
            <p className="text-white/30 text-sm mt-2">{subMessage}</p>
          )}
          {isLive && (
            <div className="mt-4 p-3 bg-yellow-500/10 rounded-lg border border-yellow-500/20">
              <p className="text-yellow-300 text-xs">
                Note: Live odds availability depends on the league and bookmaker coverage.
                Major leagues typically have better live odds support.
              </p>
            </div>
          )}
        </div>
      );
    }

    // Group odds by type for better organization
    const popularBets = ['Fulltime Result', 'Over/Under Line', 'Asian Handicap', 'Both Teams To Score', 'Double Chance'];
    const sortedOdds = [...oddsData].sort((a, b) => {
      const aIndex = popularBets.indexOf(a.name);
      const bIndex = popularBets.indexOf(b.name);
      if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
      if (aIndex !== -1) return -1;
      if (bIndex !== -1) return 1;
      return a.name.localeCompare(b.name);
    });

    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
          <span className="text-sm font-medium text-white/80">Live Betting Markets</span>
        </div>
        
        {sortedOdds.map((bet, idx) => (
          <div key={idx} className="bg-white/[0.03] rounded-xl p-4 border border-white/[0.05]">
            <h4 className="font-bold text-white mb-3">{bet.name}</h4>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {bet.values?.map((value, valueIdx) => (
                <div 
                  key={valueIdx}
                  className={`bg-white/[0.05] rounded-lg p-3 text-center hover:bg-white/[0.08] transition-colors ${
                    value.suspended ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  <p className="text-xs text-white/60 mb-1">
                    {value.value}
                    {value.handicap && ` (${value.handicap})`}
                  </p>
                  <p className={`text-lg font-bold ${
                    value.suspended ? 'text-red-400' : 'text-emerald-300'
                  }`}>
                    {value.suspended ? 'Suspended' : value.odd}
                  </p>
                  {value.main && (
                    <span className="text-[10px] text-yellow-400 font-medium">Main Line</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderOddsSection = (oddsData, title) => {
    if (!oddsData || oddsData.length === 0) {
      // More informative messages based on context
      let message = `${title} odds not available`;
      let subMessage = null;
      
      if (title === 'Live') {
        if (!isLive) {
          subMessage = 'Match is not currently live';
        } else {
          message = 'Live odds not available';
          subMessage = 'Live betting may not be offered for this match or league';
        }
      } else if (title === 'Pre-match') {
        if (isLive) {
          subMessage = 'Pre-match odds are no longer available once the match starts';
        } else if (isFinished) {
          subMessage = 'Match has already finished';
        }
      }
      
      return (
        <div className="text-center py-8">
          <p className="text-white/50">{message}</p>
          {subMessage && (
            <p className="text-white/30 text-sm mt-2">{subMessage}</p>
          )}
          {title === 'Live' && isLive && (
            <div className="mt-4 p-3 bg-yellow-500/10 rounded-lg border border-yellow-500/20">
              <p className="text-yellow-300 text-xs">
                Note: Live odds availability depends on the league and bookmaker coverage.
                Major leagues typically have better live odds support.
              </p>
            </div>
          )}
        </div>
      );
    }

    return (
      <div className="space-y-6">
        {oddsData.map((bookmaker, idx) => (
          <div key={idx} className="bg-white/[0.03] rounded-xl p-4 border border-white/[0.05]">
            <h4 className="font-bold text-white mb-3">{bookmaker.name}</h4>
            <div className="space-y-3">
              {bookmaker.bets?.map((bet, betIdx) => (
                <div key={betIdx}>
                  <p className="text-sm text-white/60 mb-2">{bet.name}</p>
                  <div className="grid grid-cols-3 gap-2">
                    {bet.values?.map((value, valueIdx) => (
                      <div 
                        key={valueIdx}
                        className="bg-white/[0.05] rounded-lg p-3 text-center hover:bg-white/[0.08] transition-colors"
                      >
                        <p className="text-xs text-white/60 mb-1">{value.value}</p>
                        <p className="text-lg font-bold text-emerald-300">{value.odd}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <>
      <AnimatePresence>
        {fixture && (
          <motion.div
            key="odds-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={onClose}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: "spring", damping: 20 }}
              className="bg-gray-900 rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
          {/* Header */}
          <div className="bg-gradient-to-r from-emerald-500 to-violet-500 p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-2xl font-black text-white">Betting Odds</h2>
                <div className="flex items-center gap-4 mt-2 text-white/80 text-sm">
                  <div className="flex items-center gap-1">
                    <Trophy size={14} />
                    <span>{fixture.league?.name}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Calendar size={14} />
                    <span>{new Date(fixture.fixture?.date).toLocaleDateString()}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Clock size={14} />
                    <span>{new Date(fixture.fixture?.date).toLocaleTimeString()}</span>
                  </div>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              >
                <X size={24} className="text-white" />
              </button>
            </div>

            {/* Teams */}
            <div className="grid grid-cols-3 gap-4 items-center">
              <div className="text-center">
                <img 
                  src={homeTeam?.logo} 
                  alt={homeTeam?.name} 
                  className="w-16 h-16 mx-auto mb-2 object-contain"
                />
                <p className="font-bold text-white">{homeTeam?.name}</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-black text-white">VS</p>
              </div>
              <div className="text-center">
                <img 
                  src={awayTeam?.logo} 
                  alt={awayTeam?.name} 
                  className="w-16 h-16 mx-auto mb-2 object-contain"
                />
                <p className="font-bold text-white">{awayTeam?.name}</p>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="border-b border-white/10">
            <div className="flex">
              {tabs.map(tab => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`
                      flex items-center gap-2 px-6 py-3 font-bold transition-all
                      ${activeTab === tab.id 
                        ? 'bg-white/10 text-white border-b-2 border-emerald-500' 
                        : 'text-white/60 hover:text-white hover:bg-white/[0.05]'
                      }
                    `}
                  >
                    <Icon size={18} />
                    {tab.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-[calc(90vh-300px)]">
            {loading ? (
              <LoadingSpinner message="Loading odds data..." />
            ) : (
              <>
                {activeTab === 'prematch' && renderOddsSection(
                  odds?.preMatch?.[0]?.bookmakers, 
                  'Pre-match'
                )}
                {activeTab === 'live' && renderLiveOddsSection(
                  odds?.live?.[0]?.odds, 
                  'Live'
                )}
                {activeTab === 'calculator' && <EVCalculator odds={odds} fixture={fixture} />}
                {activeTab === 'movement' && <OddsMovementTab fixture={fixture} />}
              </>
            )}
          </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default FixtureOddsModal;